import { useState } from "react";
import { motion } from "framer-motion";
import { Play, Sparkles, Instagram } from "lucide-react";
import { socialReels, youtubeThumb, AI_POSTER_FALLBACK } from "../data/videos";
import useIgMedia from "../hooks/useIgMedia";

// Preserve first-appearance order of categories
const CATEGORIES = socialReels.reduce((acc, r) => {
  if (!acc.includes(r.category)) acc.push(r.category);
  return acc;
}, []);

function ImageTile({ reel, index, onOpen }) {
  const [src, setSrc] = useState(reel.poster || youtubeThumb(reel.youtubeId));
  return (
    <motion.button
      data-testid={`social-card-${reel.id}`}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.7, delay: (index % 4) * 0.06, ease: [0.16, 1, 0.3, 1] }}
      onClick={() => onOpen(reel)}
      className="group block text-left"
    >
      <div className="relative aspect-[9/16] overflow-hidden rounded-xl border border-[#CBFF3A]/40">
        <img
          src={src}
          alt={reel.role}
          loading="lazy"
          onError={() => setSrc(AI_POSTER_FALLBACK)}
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-transparent to-black/20" />
        {reel.ai && (
          <div className="absolute left-3 top-3 flex items-center gap-1 rounded-full bg-[#CBFF3A] px-2.5 py-1 font-hud text-[9px] font-700 uppercase tracking-widest text-black">
            <Sparkles className="h-3 w-3" /> AI
          </div>
        )}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-500 group-hover:opacity-100">
          <div className="flex h-12 w-12 items-center justify-center rounded-full glass-panel">
            <Play className="h-4 w-4 translate-x-[1px] fill-[#CBFF3A] text-[#CBFF3A]" />
          </div>
        </div>
      </div>
      <p className="mt-3 font-hud text-[11px] uppercase tracking-wide text-[#ECEAE3]">
        {reel.label || reel.role}
      </p>
      <p className="mt-1 font-body text-xs text-white/45">{reel.note || reel.role}</p>
    </motion.button>
  );
}

// Per-category visual palette so IG tiles look intentional even when the live
// thumbnail can't resolve (Instagram aggressively rate-limits datacenter IPs).
const CATEGORY_STYLE = {
  Mastercard:              { grad: "from-[#EB001B]/60 via-[#F79E1B]/25 to-black",   accent: "#EB001B" },
  "Live & On-Camera":      { grad: "from-[#0AA4FF]/55 via-[#3E60FF]/25 to-black",   accent: "#0AA4FF" },
  "Changemaker Stories":   { grad: "from-[#F4B942]/55 via-[#E85D75]/25 to-black",   accent: "#F4B942" },
  "One District One Product": { grad: "from-[#22C55E]/55 via-[#0EA5E9]/25 to-black", accent: "#22C55E" },
  "Entrepreneur Stories":  { grad: "from-[#A855F7]/55 via-[#EC4899]/25 to-black",   accent: "#A855F7" },
  "Big Sharks":            { grad: "from-[#06B6D4]/55 via-[#3B82F6]/25 to-black",   accent: "#06B6D4" },
  "AI Content":            { grad: "from-[#CBFF3A]/55 via-[#84CC16]/25 to-black",   accent: "#CBFF3A" },
};
const DEFAULT_STYLE = { grad: "from-[#CBFF3A]/40 via-[#22252A]/40 to-black", accent: "#CBFF3A" };

function InstaTile({ reel, index, onOpen }) {
  const media = useIgMedia(reel.instaCode);
  // Static poster is pre-fetched into /public/ig-thumbs/<code>.jpg at build time —
  // guaranteed to load; falls back to the designed category gradient if missing.
  const staticPoster = `/ig-thumbs/${reel.instaCode}.jpg`;
  const [imgOk, setImgOk] = useState(true);
  const style = CATEGORY_STYLE[reel.category] || DEFAULT_STYLE;

  return (
    <motion.button
      data-testid={`social-card-${reel.id}`}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.7, delay: (index % 4) * 0.06, ease: [0.16, 1, 0.3, 1] }}
      onClick={() => onOpen({ ...reel, _resolved: media })}
      className="group block text-left"
    >
      <div className="relative aspect-[9/16] overflow-hidden rounded-xl border border-white/10 bg-[#0d0d0f] transition-colors duration-500 group-hover:border-[#CBFF3A]/50">
        {imgOk ? (
          <img
            src={staticPoster}
            alt={reel.role}
            loading="lazy"
            onError={() => setImgOk(false)}
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
          />
        ) : (
          <>
            {/* Category-themed fallback — only shown when the static poster is missing */}
            <div className={`absolute inset-0 bg-gradient-to-br ${style.grad}`} />
            <div
              aria-hidden
              className="absolute inset-0 opacity-[0.08]"
              style={{
                backgroundImage:
                  "repeating-linear-gradient(0deg, rgba(255,255,255,0.8) 0 1px, transparent 1px 3px)",
              }}
            />
            <div
              className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full blur-3xl transition-opacity duration-500 group-hover:opacity-90"
              style={{ backgroundColor: style.accent + "40" }}
            />
          </>
        )}

        {/* gradient scrim */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-black/25" />

        <div className="absolute inset-0 flex flex-col justify-between p-4">
          <div className="flex items-center justify-between">
            <Instagram className="h-5 w-5 text-white/70 transition-colors group-hover:text-[#CBFF3A]" />
            <span className="font-hud text-[9px] uppercase tracking-widest text-white/45">
              Reel
            </span>
          </div>

          <div className="flex items-end justify-between">
            <p className="max-w-[70%] font-hud text-[10px] uppercase leading-snug tracking-wide text-white/85">
              {reel.role}
            </p>
            <div className="flex h-11 w-11 items-center justify-center rounded-full border border-white/25 bg-black/50 backdrop-blur-sm transition-all duration-500 group-hover:scale-110 group-hover:border-[#CBFF3A]">
              <Play className="h-3.5 w-3.5 translate-x-[1px] fill-white text-white transition-colors group-hover:fill-[#CBFF3A] group-hover:text-[#CBFF3A]" />
            </div>
          </div>
        </div>
      </div>
    </motion.button>
  );
}

export default function SocialGrid({ onOpen }) {
  const open = (r) => onOpen({ ...r, kind: "reel" });
  return (
    <section
      id="social"
      data-testid="social-grid"
      className="mx-auto max-w-[1400px] px-6 py-28 md:px-16 md:py-32"
    >
      <div className="mb-14 flex flex-col">
        <h2 className="font-display text-4xl font-600 tracking-tight text-[#ECEAE3] md:text-5xl">
          Social First Content
        </h2>
        <p className="mt-3 font-body text-white/55">
          High-engagement, vertical-optimized, viral-DNA storytelling.
        </p>
      </div>

      <div className="space-y-16">
        {CATEGORIES.map((cat) => {
          const items = socialReels.filter((r) => r.category === cat);
          return (
            <div key={cat} data-testid={`social-category-${cat.replace(/\s+/g, "-").toLowerCase()}`}>
              <div className="mb-6 flex items-baseline justify-between border-b border-white/10 pb-3">
                <h3 className="font-display text-xl font-600 italic text-[#CBFF3A]">
                  {cat}
                </h3>
                <span className="font-hud text-[10px] uppercase tracking-[0.2em] text-white/40">
                  {String(items.length).padStart(2, "0")} {items.length > 1 ? "reels" : "reel"}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 md:gap-6">
                {items.map((r, i) =>
                  r.provider === "instagram" ? (
                    <InstaTile key={r.id} reel={r} index={i} onOpen={open} />
                  ) : (
                    <ImageTile key={r.id} reel={r} index={i} onOpen={open} />
                  )
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
